import { Box, Container, Grid, Theme, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FC, RefObject, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useWindowSize } from "react-use";
import { images } from "../../../constants/assets";
import formValidator from "../../../constants/formValidator";
import AppButton from "../../common/Button/AppButton";
import TextBox from "../../common/Inputs/TextBox";
import Text from "../../common/Text";
import SectionTitle from "./SectionTitle";



const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(19, 0),
        backgroundColor: theme.palette.custom.base,

        [theme.breakpoints.down('md')]: {
            padding: theme.spacing(6, 0),
        },
        position: 'relative',
        // backgroundColor: '#fff'
    },
    gridContainer: {

    },
    sectionSubtitle: {
        marginTop: 36,
        lineHeight: '36px',
        fontSize: 16,

        [theme.breakpoints.down('md')]: {
            marginTop: 30,
            lineHeight: '30px',
            fontSize: 14,
        }
    },
    icon: {
        marginRight: 36,
        width: 24,
        height: 24
    },
    followUsArea: {
        marginTop: 100,
        [theme.breakpoints.down('md')]: {
            marginTop: 50,
        }
    },
    contactTextOutline: {
        opacity: 0.3,
        position: 'relative',
        top: '-4rem',
        maxWidth: 550,
        height: 'auto',

        [theme.breakpoints.down('md')]: {

        }
    },
    ctaBtnArea: {
        marginTop: 40,
    },
    ctaBtn: {
        background: `${theme.palette.custom.textPrimary}`
    },
    inputBox: {
        '& .MuiInputLabel-root': {
            color: theme.palette.custom.textPrimary,
            fontSize: 16,
            top: -8
        },
        '& .MuiInput-root:before, & .MuiInput-root:after': {
            borderBottom: `1px solid ${theme.palette.custom.textPrimary}`
        }
    },
    
}))

const FollowUsArea = () => {
    const classes = useStyles()

    return (
        <Box className={classes.followUsArea}>
            <Text mb={2} fontWeight={600} fontSize={16}>Follow us</Text>
            <Box display='flex'>
                <a href='https://www.linkedin.com/company/theproreal/' target='_blank' rel="noreferrer">
                    <img className={classes.icon} alt='linkedin' src={images.LinkedIn} />
                </a>
                <a href='https://twitter.com/ETheproreal?t=NCZGHKfePlSXcUQ2UtqtHQ&s=09' target='_blank' rel="noreferrer">
                    <img className={classes.icon} alt='twitter' src={images.Twitter} />
                </a>
            </Box>
        </Box>
    )
}

interface Props {
    innerRef: RefObject<HTMLElement>
}

const ContactUsSection: FC<Props> = ({
    innerRef
}) => {
    const { control, handleSubmit, formState: { errors } } = useForm<ContactUsForm>();
    const classes = useStyles()
    const { width } = useWindowSize()
    const theme = useTheme()
    
    return (
        <Box ref={innerRef} className={classes.root} component='section' id='contactUsSection'>
            <Container>
                
                <Grid container className={classes.gridContainer}>
                    <Grid item xs={12} md={6}>
                        <SectionTitle
                            titleLine1="Say Hello,"
                        />
                        <Text className={classes.sectionSubtitle}>
                            We're just one message away. Send us a message <br /> and we’ll get back to you as soon as <br /> possible.
                        </Text>
                        {
                            width > theme.breakpoints.values.sm &&
                            <FollowUsArea />
                        }
                    </Grid>
                    <Grid item xs={12} md={6} sx={{
                        marginTop: {
                            xs: 5,
                            md: 0
                        }
                    }}>
                        <Box
                            textAlign='right'
                            sx={{
                                display: {
                                    xs: 'none',
                                    md: 'block'
                                }
                            }}
                        >
                            <img className={classes.contactTextOutline} alt='outline' src={images.ContactTextOutline} />
                        </Box>
                        <Grid rowSpacing={5} spacing={3} container>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name='fullName'
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { value, onChange, name } }) => (
                                        <TextBox
                                            name={name}
                                            label='Full Name *'
                                            value={value}
                                            onChange={onChange}
                                            errors={errors}
                                            parentClassName={classes.inputBox}
                                        />
                                    )}
                                    rules={{
                                        required: formValidator.required
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name='email'
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { value, onChange, name } }) => (
                                        <TextBox
                                            name={name}
                                            label='Email *'
                                            value={value}
                                            onChange={onChange}
                                            errors={errors}
                                            type='email'
                                            parentClassName={classes.inputBox}
                                        />
                                    )}
                                    rules={{
                                        required: formValidator.required
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name='mobile'
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { value, onChange, name } }) => (
                                        <TextBox
                                            name={name}
                                            label='Phone number *'
                                            value={value}
                                            onChange={onChange}
                                            errors={errors}
                                            type='number'
                                            parentClassName={classes.inputBox}
                                        />
                                    )}
                                    rules={{
                                        required: formValidator.required
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name='company'
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { value, onChange, name } }) => (
                                        <TextBox
                                            name={name}
                                            label='Company *'
                                            value={value}
                                            onChange={onChange}
                                            errors={errors}
                                            parentClassName={classes.inputBox}
                                        />
                                    )}
                                    rules={{
                                        required: formValidator.required
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name='message'
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { value, onChange, name } }) => (
                                        <TextBox
                                            name={name}
                                            label='Message *'
                                            value={value}
                                            onChange={onChange}
                                            errors={errors}
                                            multiline
                                            parentClassName={classes.inputBox}
                                        />
                                    )}
                                    rules={{
                                        required: formValidator.required
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} className={classes.ctaBtnArea}>
                                <AppButton className={classes.ctaBtn} variant='contained'>Send message</AppButton>
                                {
                                    width < theme.breakpoints.values.sm &&
                                    <FollowUsArea />
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default ContactUsSection